import Carousel from '@allesauto/core/carousel';
import Headline from '@allesauto/core/headline';
import TextLink from '@allesauto/core/textLink';
import { Colors, FieldTypeLink, theme } from '@allesauto/utils/consts';
import { getUrlFromStoryblokLink } from '@allesauto/utils/funcs';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';

import TextsliderTile, { TextsliderTileProps } from './TextsliderTile';

interface TextsliderProps {
  headline: string;
  headlineTag: 2 | 3 | 4;
  headlineFontSize: 'title' | 'headline2' | 'headline3' | 'headline4';
  headlineColor: Colors;
  background: Colors;
  text?: string;
  textColor?: Colors.WHITE | Colors.BLACK;
  link?: FieldTypeLink;
  linkText?: string;
  linkColor?: Colors;
  tiles: TextsliderTileProps[];
  _uid: string;
  component: string;
}

const cssClasses = {
  container:
    'grid py-30px sm:px-[16px] md:px-0 lg:py-60px grid-cols-1 md:grid-cols-24 gap-[20px]',
  carouselContainer:
    'grid-cols-24  md:col-start-2 mt-40px lg:mt-0 lg:col-start-10 lg:col-end-25 lg:pr-[16px] xl:pr-[32px] md:col-end-24',
  content: 'md:col-start-2 md:col-end-24 lg:col-end-10',
  text: 'mt-[20px]',
  nextButton: '!p-0 !rounded-circle h-[28px] w-[28px]',
};

const carouselClasses = {
  sectionClass: 'bg-transparent w-full !pb-0',
  containerClass:
    '!mt-0 col-start-1 sm:col-end-25 lg:col-start-2 lg:col-end-24',
  dotListClass: clsx(
    'react-multi-carousel-dot-dark-bg',
    '!mb-0 gap-10px p-30px'
  ),
  itemClass: 'sm:px-[14px] md:px-[14px] lg:px-0',
  dotClass: 'rounded-default bg-red60',
  buttonLeftClass: clsx(cssClasses.nextButton, 'col-start-2 lg:col-start-1'),
  buttonRightClass: clsx(
    cssClasses.nextButton,
    'col-end-25 md:col-end-25 lg:col-end-25'
  ),
};

const Textslider = (props: TextsliderProps) => {
  const {
    headline,
    headlineColor,
    headlineTag,
    background,
    headlineFontSize,
    text,
    textColor = Colors.BLACK,
    link,
    linkText,
    tiles,
    linkColor = Colors.PURPLE,
  } = props;

  const renderLink = link && linkText;

  return (
    <section
      {...sbEditable(props)}
      className={clsx(cssClasses.container, theme[background].background)}
    >
      <div className={cssClasses.content}>
        <Headline
          headlineTag={headlineTag}
          headlineText={headline}
          headlineColor={theme[headlineColor].text}
          headlineClassName={`text-${headlineFontSize}`}
        />
        <p className={clsx(theme[textColor].text, cssClasses.text)}>{text}</p>
        {renderLink && (
          <TextLink
            textColor={theme[linkColor].text}
            customClassName={clsx(cssClasses.text, theme[linkColor].fill)}
            fillColor={theme[linkColor].text}
            text={linkText}
            href={getUrlFromStoryblokLink(link)}
          />
        )}
      </div>
      <div className={cssClasses.carouselContainer}>
        <Carousel
          removeArrowOnDeviceType={[]}
          itemsXLDesktop={1}
          itemsLDesktop={1}
          itemsMobile={1}
          itemsTable={1}
          itemsToScrollLDesktop={1}
          itemsToScrollMobile={1}
          itemsToScrollTablet={1}
          itemsToScrollXLDesktop={1}
          isInfinite={false}
          darkBackground={background === Colors.PURPLE}
          classNames={carouselClasses}
        >
          {tiles.map((tile) => (
            <TextsliderTile key={tile._uid} {...tile} />
          ))}
        </Carousel>
      </div>
    </section>
  );
};

export default Textslider;
